import type { MinecraftServersData, MinecraftServerStats, ServerData, ServerStats } from '../types/statusMonitor';
import type { ServerListEntry } from '../services/serverListApi';
import { formatBackupAgeCompact, getBackupAgeSeverity } from './minecraftBackupStatus';
import { formatMemoryUsage, normalizeHostname, parseSize } from './statusMonitor';
import type { ComingSoonMap } from '../services/maintenanceApi';

/**
 * `upcoming` is a server that is coming soon: shown to the public, not open
 * yet. It is neither healthy nor a problem, so it is its own answer rather
 * than an override of the other three, and nothing that counts outages or
 * alerts ever counts it. Hosts are never upcoming.
 */
export type Health = 'online' | 'attention' | 'offline' | 'upcoming';
export type StatusFilter = 'all' | 'attention' | 'offline' | 'silent' | 'maintenance' | 'coming-soon' | 'performance' | 'backup' | 'outdated';
export type ServerSortKey = 'name' | 'host' | 'health' | 'players' | 'tps' | 'mspt' | 'backup' | 'seen';
export type SortDirection = 'asc' | 'desc';
export type ClusterOrder = 'name' | 'problems';
export const compareNames = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
/** A Quasar version that can be ordered: "5.12.5", not "Legacy". */
const isNumberedVersion = (version: string) => /^v?\d/i.test(version);
export const hostKey = (hostname: string) => normalizeHostname(hostname.trim().toLowerCase());
export const finiteMetric = (value: unknown): number | null => typeof value === 'number' && Number.isFinite(value) ? value : null;
/**
 * How long a server may be quiet before the page says anything.
 *
 * Instances report about once a minute, so a single missed report is ordinary
 * and must not colour the page: at ninety seconds the old thresholds flagged a
 * server that was simply between heartbeats. Nothing is said for three
 * minutes, and only after five is a server called offline, which is two missed
 * reports past the first complaint.
 */
export const HEARTBEAT_DELAYED_MS = 180_000;
export const HEARTBEAT_OFFLINE_MS = 300_000;
/** Long enough that "it is restarting" stops being the likely explanation. */
export const HEARTBEAT_EXTENDED_MS = 900_000;

export const isReportFresh = (lastSeen: number, now: number) => Number.isFinite(lastSeen) && lastSeen > 0 && now - lastSeen * 1000 <= HEARTBEAT_OFFLINE_MS;
export const reportAge = (lastSeen: number, now: number) => lastSeen > 0 && Number.isFinite(lastSeen)
  ? `${formatBackupAgeCompact(Math.max(0, now - lastSeen * 1000))} ago` : 'Unknown';

export function backupState(stats: MinecraftServerStats, now: number) {
  const timestamp = finiteMetric(stats.last_backup_taken);
  const age = timestamp !== null && timestamp > 0 && timestamp * 1000 <= now ? now - timestamp * 1000 : null;
  const severity = age === null ? 'unknown' : getBackupAgeSeverity(age);
  return {
    age,
    severity,
    // A recent timestamp does not prove that a backup is currently running.
    label: age === null ? 'Unknown' : `${formatBackupAgeCompact(age)} ago`,
  };
}

export function serverHealth(stats: MinecraftServerStats, now: number) {
  const online = isReportFresh(stats.last_seen, now);
  const reportMissing = !Number.isFinite(stats.last_seen) || stats.last_seen <= 0;
  const delayed = online && now - stats.last_seen * 1000 > HEARTBEAT_DELAYED_MS;
  const extendedDowntime = !reportMissing && now - stats.last_seen * 1000 > HEARTBEAT_EXTENDED_MS;
  const tps = finiteMetric(stats.tps);
  const mspt = finiteMetric(stats.mspt);
  const performance = online && ((tps !== null && tps < 18) || (mspt !== null && mspt > 50));
  const criticalPerformance = online && ((tps !== null && tps < 10) || (mspt !== null && mspt > 100));
  const backup = backupState(stats, now);
  const backupConcern = online && (backup.severity === 'concern' || backup.severity === 'critical');
  const critical = criticalPerformance || (backupConcern && backup.severity === 'critical');
  const missingMetrics = online && (tps === null || mspt === null);
  const health: Health = !online ? 'offline' : performance || backupConcern || delayed || missingMetrics ? 'attention' : 'online';
  const reasons = !online ? [reportMissing ? 'No heartbeat reported' : extendedDowntime ? 'No report for over 15 minutes' : 'No report for over 5 minutes'] : [
    ...(delayed ? ['Heartbeat delayed'] : []),
    ...(tps !== null && tps < 18 ? [tps < 10 ? 'Severely low TPS' : 'Low TPS'] : []),
    ...(mspt !== null && mspt > 50 ? [mspt > 100 ? 'Severely high MSPT' : 'High MSPT'] : []),
    ...(missingMetrics ? ['Performance data missing'] : []),
    ...(backupConcern ? [backup.severity === 'critical' ? 'Backup over 7 days old' : 'Backup over 24 hours old'] : []),
  ];
  return { health, online, performance, critical, delayed, extendedDowntime, backupConcern, backup, reasons };
}

export const serviceState = (status: string) => status?.toUpperCase() === 'OPEN' ? 'open' : status?.toUpperCase() === 'CLOSED' ? 'closed' : 'unknown';

export function hostHealth(stats: ServerStats, now: number) {
  const online = isReportFresh(stats.last_seen, now);
  const memory = formatMemoryUsage(stats.mem, stats.mem_effective, stats.mem_htop);
  const cpu = Number.parseFloat(stats.cpu);
  const disks = (stats.disks ?? []).filter(disk => parseSize(disk.size) >= 10);
  const diskUsage = disks.map(disk => Number.parseFloat(disk.usage_percent)).filter(Number.isFinite);
  const disk = diskUsage.length ? Math.max(...diskUsage) : null;
  const services = stats.ports ?? [];
  const closedServices = services.filter(port => serviceState(port.status) === 'closed');
  const delayed = online && now - stats.last_seen * 1000 > HEARTBEAT_DELAYED_MS;
  const reasons = !online ? ['No report for over 5 minutes'] : [
    ...(delayed ? ['Heartbeat delayed'] : []),
    ...(cpu > 90 ? ['CPU over 90%'] : []),
    ...(memory.effectivePercent > 90 ? ['Memory over 90%'] : []),
    ...disks.filter(disk => Number.parseFloat(disk.usage_percent) > 85).map(disk => `${disk.device}: ${disk.usage_percent} used`),
    ...closedServices.map(port => `${port.identifier}: closed`),
  ];
  const health: Health = !online ? 'offline' : reasons.length ? 'attention' : 'online';
  return { online, memory, cpu: finiteMetric(cpu), disk, disks, services, health, reasons };
}

/** One server as the public server list knows it: the inventory the heartbeat feed cannot provide. */
export interface ServerListing {
  address: string;
  title: string;
  cluster: string;
  region: string;
  online: boolean;
  players: number;
  maxPlayers: number;
  minecraftVersion: string | null;
  lastOnlineAt: number | null;
  lastOfflineAt: number | null;
}

const normalizeKey = (value: string | null | undefined) => (value ?? '').trim().toLowerCase();
const placeKey = (cluster: string | null | undefined, region: string | null | undefined) => `${normalizeKey(cluster)}|${normalizeKey(region)}`;

/** The list's timestamps carry no zone designator and are UTC. */
export function listTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const iso = /(?:[zZ]|[+-]\d\d:?\d\d)$/.test(value) ? value : `${value}Z`;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/** One listing per address. Cluster children are the real servers; a parent without children is its own server. */
export function indexServerListings(entries: ServerListEntry[]): Map<string, ServerListing> {
  const listings = new Map<string, ServerListing>();
  for (const entry of entries) {
    const members = entry.clusterChildren?.length ? entry.clusterChildren : [entry];
    for (const member of members) {
      const address = normalizeKey(member.serverAddress);
      if (!address || listings.has(address)) continue;
      listings.set(address, {
        address,
        title: entry.modpackTitle || entry.clusterName || address,
        cluster: entry.clusterName || '',
        region: member.regionName || entry.regionName || '',
        online: Boolean(member.isServerOnline),
        players: finiteMetric(member.onlinePlayerCount) ?? 0,
        maxPlayers: finiteMetric(member.maxPlayerCount) ?? 0,
        minecraftVersion: entry.minecraftVersion ?? null,
        lastOnlineAt: listTimestamp(member.lastOnlineTime),
        lastOfflineAt: listTimestamp(member.lastOfflineTime),
      });
    }
  }
  return listings;
}

/**
 * A feed-shaped record for a server the feed has never mentioned, so every reader of `stats` keeps
 * working. Metrics are NaN and render as dashes; the listing supplies identity and player counts.
 */
function listingStats(listing: ServerListing, clusterId: string): MinecraftServerStats {
  const popularity = { server_id: listing.address, popularity_index: -1 };
  return {
    serverId: listing.region, clusterId, timestamp: 0, motd: '', modloader: '', modloaderVersion: '',
    mcVersion: listing.minecraftVersion ?? '', maxPlayers: listing.maxPlayers, quasarVersion: '', redisPing: NaN,
    serverPort: 0, hostname: '', players: [], tps: NaN, mspt: NaN, entities: NaN, chunks: NaN,
    playerCount: listing.players, popularity, popularity_7d: popularity, popularity_28d: popularity,
    identifier: `${listing.cluster}-${listing.region}`,
    // The list's last-offline moment is the last time it saw the server up.
    last_seen: !listing.online && listing.lastOfflineAt ? listing.lastOfflineAt / 1000 : 0,
    fqdn: listing.address, modPackName: listing.title,
  };
}

/**
 * The heartbeat says whether telemetry is arriving; the list says whether players can reach the
 * server. Disagreement is itself the signal: a listed-online server with no heartbeat is a
 * monitoring gap, not an outage, and a heartbeating server the list shows offline is invisible to players.
 */
function crossCheck(state: ReturnType<typeof serverHealth>, listing: ServerListing | null, reported: boolean): { health: Health; reasons: string[] } {
  if (!listing) return { health: state.health, reasons: state.reasons };
  if (state.online) {
    return listing.online
      ? { health: state.health, reasons: state.reasons }
      : { health: 'attention', reasons: [...state.reasons, 'Offline in server list'] };
  }
  if (listing.online) return { health: 'attention', reasons: [...(reported ? state.reasons : ['Not reporting to status monitor']), 'Listed online'] };
  return { health: 'offline', reasons: ['Offline in server list', ...(reported ? state.reasons : [])] };
}

/**
 * Rows come from the heartbeat feed joined to the server list, so a server the feed has forgotten
 * (an enrolment restart, an eviction, or an instance that never enrolled) still has a row. The
 * join is by address, which every feed entry carries as `fqdn`; cluster and region only stand in
 * when it is missing. Without an inventory the rows are the feed alone, exactly as before.
 */
export function buildServerRows(
  servers: MinecraftServersData,
  now: number,
  inventory?: ServerListEntry[],
  maintenance?: Record<string, boolean> | null,
  comingSoon?: ComingSoonMap | null,
) {
  // Only numbered versions set the bar. A label such as "Legacy" or "Unknown"
  // sorts after every number, so letting one in made it the fleet's "newest"
  // and flagged every numbered server as behind it.
  const latestVersion = Object.values(servers).reduce((latest, stats) => {
    const version = (stats.quasarVersion || '').trim();
    return isNumberedVersion(version) && compareNames(version, latest) > 0 ? version : latest;
  }, '');
  // Maintenance is keyed by the same address the listing join uses, so a row
  // that found its listing can always find its flag.
  const inMaintenance = (address: string | null | undefined) =>
    Boolean(address && maintenance?.[normalizeKey(address)]);
  const soonEntry = (address: string | null | undefined) =>
    (address && comingSoon?.[normalizeKey(address)]) || null;
  /**
   * A coming-soon server is in a window (the flag is on, restarts are
   * refused) of the kind that is news rather than trouble. Its health is
   * `upcoming` whatever its heartbeat says, and it is never critical: a new
   * pack that is not running yet, or is running whitelisted with a slow tick
   * while staff build spawn, is not an incident. The measured reading is kept
   * in `measuredHealth` and the reasons for anyone who opens the row.
   */
  const windowFields = (health: Health, critical: boolean, ...addresses: (string | null | undefined)[]) => {
    const soon = addresses.map(soonEntry).find(Boolean) ?? null;
    return {
      maintenance: Boolean(soon) || addresses.some(inMaintenance),
      comingSoon: Boolean(soon),
      releaseAt: soon?.releaseAt ?? null,
      measuredHealth: health,
      health: soon ? 'upcoming' as const : health,
      critical: soon ? false : critical,
    };
  };
  const listings = inventory ? indexServerListings(inventory) : new Map<string, ServerListing>();
  const byPlace = new Map<string, ServerListing>();
  for (const listing of listings.values()) if (!byPlace.has(placeKey(listing.cluster, listing.region))) byPlace.set(placeKey(listing.cluster, listing.region), listing);
  const claimed = new Set<string>();
  const clusterSpelling = new Map<string, string>();

  // Iterate the instance feed directly: a missing host report must never hide its servers.
  const reported = Object.entries(servers).map(([id, stats]) => {
    const state = serverHealth(stats, now);
    const outdated = Boolean(state.online && stats.quasarVersion && latestVersion && compareNames(stats.quasarVersion, latestVersion) < 0);
    const address = normalizeKey(stats.fqdn || stats.popularity?.server_id);
    const listing = (address && listings.get(address)) || byPlace.get(placeKey(stats.clusterId, stats.serverId)) || null;
    if (listing) claimed.add(listing.address);
    if (stats.clusterId && !clusterSpelling.has(normalizeKey(stats.clusterId))) clusterSpelling.set(normalizeKey(stats.clusterId), stats.clusterId);
    const checked = crossCheck(state, listing, true);
    return {
      id,
      stats,
      name: `${stats.modPackName || stats.clusterId || stats.identifier} · ${stats.serverId}`,
      host: hostKey(stats.hostname || ''),
      ...state,
      outdated,
      reported: true,
      listing,
      address: address || listing?.address || '',
      reasons: [...checked.reasons, ...(outdated ? ['Quasar behind fleet'] : [])],
      ...windowFields(outdated && checked.health === 'online' ? 'attention' : checked.health, state.critical, address, listing?.address),
    };
  });

  const unreported = [...listings.values()].filter(listing => !claimed.has(listing.address)).map(listing => {
    // Keep the feed's spelling of the cluster so the listed sibling lands in the same panel.
    const clusterId = clusterSpelling.get(normalizeKey(listing.cluster)) ?? listing.cluster;
    const stats = listingStats(listing, clusterId);
    const state = serverHealth(stats, now);
    const checked = crossCheck(state, listing, false);
    return {
      id: `list:${listing.address}`,
      stats,
      name: `${listing.title || clusterId} · ${listing.region}`,
      host: '',
      ...state,
      outdated: false,
      reported: false,
      listing,
      address: listing.address,
      reasons: checked.reasons,
      ...windowFields(checked.health, state.critical, listing.address),
    };
  });

  return [...reported, ...unreported];
}

export type ServerRow = ReturnType<typeof buildServerRows>[number];

/** Group the already filtered/sorted rows, retaining the chosen sort within each cluster. */
export function groupServerRows(rows: ServerRow[], order: ClusterOrder = 'name') {
  const groups = new Map<string, { id: string; name: string; rows: ServerRow[] }>();
  for (const row of rows) {
    const id = row.stats.clusterId || '';
    const group = groups.get(id) ?? { id, name: row.stats.modPackName || id || 'Unassigned', rows: [] };
    group.rows.push(row);
    groups.set(id, group);
  }
  return [...groups.values()].map(group => ({
    ...group,
    offlineCount: group.rows.filter(row => row.health === 'offline').length,
    attentionCount: group.rows.filter(row => row.health === 'attention').length,
    criticalCount: group.rows.filter(row => row.critical).length,
    // The two kinds of window are counted apart, so a card can say "1 of 3
    // coming soon" without calling the new server maintenance or vice versa.
    maintenanceCount: group.rows.filter(row => row.maintenance && !row.comingSoon).length,
    comingSoonCount: group.rows.filter(row => row.comingSoon).length,
    // Every address in the cluster that maintenance can be applied to, so one
    // press covers the whole pack rather than each region in turn.
    addresses: group.rows.map(row => row.address).filter(Boolean),
  })).sort((a, b) => {
    if (order === 'problems') {
      // Any offline instance takes priority, then performance/backup alerts.
      // Keep healthy siblings in the group; this is ordering, not filtering.
      const severity = (group: typeof a) => group.offlineCount > 0 ? 3 : group.criticalCount > 0 ? 2 : group.attentionCount > 0 ? 1 : 0;
      const severityDifference = severity(b) - severity(a);
      if (severityDifference) return severityDifference;
      const problemDifference = (b.offlineCount + b.attentionCount) - (a.offlineCount + a.attentionCount);
      if (problemDifference) return problemDifference;
    }
    return compareNames(a.name, b.name) || compareNames(a.id, b.id);
  });
}

export function selectServerRows(rows: ServerRow[], query: string, host: string, filter: StatusFilter, sort: ServerSortKey, direction: SortDirection) {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filtered = rows.filter(row => {
    const { stats } = row;
    const text = [row.name, row.host, stats.hostname, stats.identifier, stats.clusterId, stats.serverId,
      stats.fqdn, stats.popularity?.server_id, stats.modloader, stats.mcVersion].join(' ').toLowerCase();
    return (!host || row.host === host) && terms.every(term => text.includes(term)) && (
      filter === 'all' ||
      (filter === 'attention' && (row.health === 'attention' || row.health === 'offline')) ||
      (filter === 'offline' && row.health === 'offline') ||
      (filter === 'silent' && !row.online && !row.comingSoon) ||
      (filter === 'maintenance' && row.maintenance && !row.comingSoon) ||
      (filter === 'coming-soon' && row.comingSoon) ||
      (filter === 'performance' && row.performance) ||
      (filter === 'backup' && row.backupConcern) ||
      (filter === 'outdated' && row.outdated)
    );
  });
  // Upcoming servers sort after healthy ones: they are not a problem to look at first.
  const rank = { offline: 0, attention: 1, online: 2, upcoming: 3 };
  const value = (row: ServerRow): number | string | null => {
    switch (sort) {
      case 'name': return row.name;
      case 'host': return row.host;
      case 'health': return row.health === 'offline' ? 0 : row.critical ? 1 : rank[row.health] + 1;
      case 'players': return row.online ? finiteMetric(row.stats.playerCount) : null;
      case 'tps': return row.online ? finiteMetric(row.stats.tps) : null;
      case 'mspt': return row.online ? finiteMetric(row.stats.mspt) : null;
      case 'backup': return row.backup.age;
      case 'seen': return finiteMetric(row.stats.last_seen);
    }
  };
  return filtered.sort((a, b) => {
    const av = value(a), bv = value(b);
    // Missing values stay last in either direction.
    if (av === null && bv !== null) return 1;
    if (bv === null && av !== null) return -1;
    const compared = av === null || bv === null ? 0 : typeof av === 'string' && typeof bv === 'string'
      ? compareNames(av, bv) : Number(av) - Number(bv);
    return compared * (direction === 'asc' ? 1 : -1) || compareNames(a.name, b.name) || compareNames(a.id, b.id);
  });
}

export function buildHostRows(hosts: ServerData, servers: ServerRow[], now: number) {
  return Object.entries(hosts).map(([name, stats]) => {
    const instances = servers.filter(server => server.host === hostKey(name));
    return { name, stats, instances, ...hostHealth(stats, now) };
  }).sort((a, b) => compareNames(hostKey(a.name), hostKey(b.name)));
}

export type HostRow = ReturnType<typeof buildHostRows>[number];
